import { formatCurrency } from '@/utils/format-currency';
import { PaymentSimulationRejectReason } from '@/utils/payment-simulator';
import { PaymentPricing } from '@/types/payment-account';
import { PAYMENT_METHOD } from '@/types/payment-method';
import {
  formatBoletoRange,
  formatCardBrands,
  formatFeeLine,
  formatFeeSummary,
  formatPixLimit,
  formatSettlementLine,
} from './shared';

/**
 * Textos da tela "Como funciona" (plano, seção 6.2 — rota `(provider)/payments/about.tsx`, T4.2).
 * Serve também a quem já ativou (sem o botão de ativar).
 */

/** Frase única sob o título — a tela é uma lista, o detalhe mora nos modais. */
export const ABOUT_INTRO = 'O cliente paga no link. Você não confere comprovante.';

export interface ComparisonRow {
  label: string;
  manual: string;
  automatic: (pricing: PaymentPricing) => string;
}

/** A tabela manual × automático (seção 6.2, item 1). */
export const ABOUT_COMPARISON_ROWS: ComparisonRow[] = [
  {
    label: 'Quem confirma',
    manual: 'Você, pelo comprovante',
    automatic: () => 'Ninguém. Confirma sozinho',
  },
  {
    label: 'Como o cliente paga',
    manual: 'Pix para a sua chave',
    automatic: (pricing) =>
      `Pix, cartão ${formatCardBrands(pricing.cardBrands)} ou boleto no link da cobrança`,
  },
  {
    label: 'Quando cai',
    manual: 'Na hora, na sua conta',
    automatic: (pricing) => formatSettlementLine(pricing),
  },
  {
    label: 'Taxa',
    manual: 'Nenhuma',
    automatic: (pricing) => formatFeeLine(pricing),
  },
  {
    label: 'Lembrete de atraso',
    manual: 'Sim',
    automatic: () => 'Sim, e para sozinho quando pagar',
  },
  {
    label: 'Devolver dinheiro',
    manual: 'Você faz um Pix',
    automatic: () => 'Pix e cartão pelo app; boleto não tem devolução automática',
  },
];

/** Simulador (seção 6.2, item 2). A linha mostra o Pix de `previewAmount`; o modal simula qualquer valor. */
export const ABOUT_SIMULATOR = {
  title: 'Simular um valor',
  previewAmount: 100,
  fieldPlaceholder: '100,00',
  emptyAmount: 'Digite um valor para ver a simulação.',
};

/**
 * Motivo, em tom calmo, de uma forma ficar fora do simulador (valor abaixo do
 * mínimo, acima do limite ou indisponível) — item 2 da seção 6.2. Os números
 * vêm sempre do `pricing`, nunca escritos à mão.
 */
export function getSimulatorRejectReason(
  reason: PaymentSimulationRejectReason | undefined,
  method: PAYMENT_METHOD,
  pricing: PaymentPricing,
): string {
  const isBoleto = method === PAYMENT_METHOD.BANK_SLIP;
  switch (reason) {
    case 'BELOW_MIN':
      return isBoleto
        ? `Abaixo do mínimo do boleto (${formatCurrency(pricing.boletoMin)}).`
        : `Abaixo do mínimo do Pix (${formatCurrency(pricing.pixMin)}).`;
    case 'ABOVE_MAX':
      return isBoleto
        ? `Acima do máximo do boleto (${formatCurrency(pricing.boletoMax)}).`
        : `Acima do máximo do Pix (${formatCurrency(pricing.pixMax ?? 0)}).`;
    case 'METHOD_UNAVAILABLE':
    default:
      return 'Não disponível para esse valor.';
  }
}

export interface AboutDetailRow {
  label: string;
  value: string;
}

/** Limites e bandeiras (seção 6.2, item 3). */
export function getAboutLimitRows(pricing: PaymentPricing): AboutDetailRow[] {
  return [
    { label: 'Pix', value: formatPixLimit(pricing) },
    { label: 'Cartão', value: `${formatCardBrands(pricing.cardBrands)}, à vista` },
    { label: 'Boleto', value: formatBoletoRange(pricing) },
  ];
}

export const ABOUT_LIMITS_NOTE =
  'Quem não puder pagar pelo automático continua pagando direto a você, como hoje.';

/** Taxa por forma de pagamento — os números vêm sempre do `pricing`. */
export function getAboutFeeRows(pricing: PaymentPricing): AboutDetailRow[] {
  return [
    { label: 'Pix', value: formatFeeSummary({ percent: pricing.fee.pixPercent, fixed: 0 }) },
    {
      label: 'Cartão',
      value: formatFeeSummary({ percent: pricing.fee.cardPercent, fixed: pricing.fee.cardFixed }),
    },
    { label: 'Boleto', value: formatFeeSummary({ percent: 0, fixed: pricing.fee.boletoFixed }) },
  ];
}

/**
 * Quem cobra a taxa. Nunca cita o nome da processadora no app; "não é da
 * Controlapag" só aparece enquanto a margem da plataforma for zero.
 */
export function getAboutFeeNote(pricing: PaymentPricing): string {
  const owner =
    pricing.marginPercent > 0
      ? 'É a taxa do processamento do pagamento.'
      : 'É a taxa da processadora que recebe o pagamento do cliente por você, não da Controlapag.';
  return `${owner} Sai descontada do que você recebe, e você vê bruto, taxa e líquido em cada pagamento. No manual: nenhuma.`;
}

export function getAboutSettlementRows(pricing: PaymentPricing): AboutDetailRow[] {
  return [
    { label: 'Pix e boleto', value: `${pricing.settlement.pixBusinessDays} dias úteis` },
    { label: 'Cartão', value: `${pricing.settlement.cardDays} dias` },
  ];
}

export const ABOUT_SETTLEMENT_NOTE = 'O repasse para o seu banco sai sozinho, assim que o valor libera.';

/** "O que vamos pedir" (seção 6.2, item 4). */
export const ABOUT_WHAT_WE_ASK = {
  title: 'O que vamos pedir',
  summary: '2 min',
  rows: [
    { label: 'Você', value: 'CPF, nascimento e endereço' },
    { label: 'Para receber', value: 'Conta do banco' },
    { label: 'Tempo', value: 'Uns 2 minutos' },
  ] as AboutDetailRow[],
  note: 'Para confirmar que é você, pode ser pedido um código por SMS ou e-mail e, às vezes, uma selfie — tudo no app. Seus dados vão direto para a processadora de pagamentos e ficam protegidos.',
};

export const ABOUT_ACTIVATE_BUTTON = 'Ativar recebimento automático';

/**
 * Conta em `DRAFT` (T4.3): o assistente já foi enviado (`POST /payment-account`
 * feito, conta criada) mas o Stripe ainda não recebeu `details_submitted` —
 * falta a etapa 6 (confirmação no componente do Stripe, T4.4). O botão volta
 * para `/payments/activate`, que detecta a conta existente e pula direto
 * para essa etapa em vez de repetir o formulário.
 */
export const ABOUT_CONTINUE_ACTIVATION_BUTTON = 'Continuar ativação';

/** `pricing.enabled === false` (seção 6.2, item 8): tela informativa, sem botão. */
export const ABOUT_COMING_SOON_NOTE =
  'Em breve. Essa novidade ainda não está disponível para todo mundo.';
